"use client";

import React from "react";
import { useQuery } from "@tanstack/react-query";
import { learningApi } from "@/lib/api/services/learning.api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, BookOpen, CheckCircle, Loader2 } from "lucide-react";
import Link from "next/link";

interface Course {
  id: string;
  title: string;
  progress: number;
  status: string;
  instructor: string;
  thumbnail?: string;
}

export default function MyLearningPage() {
  const { data: courses = [], isLoading, isError } = useQuery({
    queryKey: ["my-learning"],
    queryFn: async () => {
      const data = await learningApi.getMyLearning();
      return ((data as unknown as { data?: Course[] }).data ?? []) as Course[];
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-foreground">My Learning</h1>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="h-20 bg-muted/10 rounded animate-pulse mb-3" />
                <div className="h-4 w-2/3 bg-muted/10 rounded animate-pulse" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-foreground">My Learning</h1>
        <Card>
          <CardContent className="py-12 text-center">
            <Loader2 className="mx-auto h-12 w-12 text-destructive/50 animate-spin" />
            <h3 className="mt-4 text-lg font-medium text-foreground">Failed to load courses</h3>
            <p className="mt-2 text-sm text-muted-foreground">Please try again later.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" asChild>
          <Link href="/learning">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back
          </Link>
        </Button>
      </div>

      <h1 className="text-2xl font-bold text-foreground">My Learning</h1>

      {courses.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <BookOpen className="mx-auto h-12 w-12 text-muted-foreground" />
            <h3 className="mt-4 text-lg font-medium text-foreground">No courses enrolled</h3>
            <p className="mt-2 text-sm text-muted-foreground">Browse the learning catalog to enroll in courses.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {courses.map((course) => (
            <Card key={course.id}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <BookOpen className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-medium text-foreground">{course.title}</h3>
                </div>
                <p className="text-xs text-muted-foreground">{course.instructor}</p>
                <div className="mt-3">
                  <div className="h-2 w-full bg-muted/10 rounded-full">
                    <div className="h-2 rounded-full bg-primary" style={{ width: `${course.progress}%` }} />
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">{course.progress}% complete</p>
                </div>
                <Badge variant="outline" className="mt-2 capitalize">{course.status}</Badge>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}